import { useMemo } from "react";

import type { V3WorkbenchData } from "../thread-v3/types";
import { Icons } from "../thread-v4/icons";
import { rowKey, selectedLineageNodeIds } from "../thread-v4/ThreadV4Widgets";

interface ProvenanceProps {
  data: V3WorkbenchData;
  selectedRunId: string | null;
  noteCount?: number;
  compact?: boolean;
  full?: boolean;
  onExpand?: () => void;
  onSelectRun?: (runId: string) => void;
}

interface Bundle {
  id: string;
  label: string;
  sub: string;
  total: number;
  x: number;
  y: number;
  r: number;
  tone: string;
}

const BUNDLE_EDGES: Array<[string, string, string]> = [
  ["geometry", "meshes", "geometry -> mesh"],
  ["meshes", "runs", "mesh -> runs"],
  ["runs", "artifacts", "run outputs"],
  ["runs", "notes", "run notes"],
  ["artifacts", "notes", "discussion"],
];

function countNodes(data: V3WorkbenchData, includes: string[]): number {
  return data.provenance.nodes.filter((node) => includes.includes(node.type)).length;
}

function bundleRadius(total: number, compact: boolean): number {
  const base = compact ? 15 : 36;
  const scale = compact ? 8 : 18;
  return base + Math.min(scale, Math.log10(Math.max(total, 1)) * scale * 0.62);
}

function makeBundles(data: V3WorkbenchData, noteCount: number, compact = false): Bundle[] {
  const geometry = countNodes(data, ["geometry", "source_dataset"]) || 1;
  const meshes = countNodes(data, ["mesh"]) || 1;
  const runs = data.totalRowCount || data.rows.length || countNodes(data, ["solver_run"]);
  const artifacts = data.assets.length || countNodes(data, ["plot", "artifact", "uvf_view", "report", "spreadsheet"]);
  const notes = noteCount || countNodes(data, ["notebook", "chat_thread", "email_thread", "decision_summary"]);
  const points: Record<"geometry" | "meshes" | "runs" | "artifacts" | "notes", [number, number]> = compact
    ? {
        geometry: [28, 30],
        meshes: [72, 58],
        runs: [122, 40],
        artifacts: [162, 74],
        notes: [204, 38],
      }
    : {
        geometry: [120, 170],
        meshes: [285, 290],
        runs: [480, 190],
        artifacts: [675, 310],
        notes: [820, 165],
      };

  return [
    { id: "geometry", label: "Geometry", sub: "source", total: geometry, x: points.geometry[0], y: points.geometry[1], r: bundleRadius(geometry, compact), tone: "ink" },
    { id: "meshes", label: "Meshes", sub: "surface + volume", total: meshes, x: points.meshes[0], y: points.meshes[1], r: bundleRadius(meshes, compact), tone: "blue" },
    { id: "runs", label: "Runs", sub: "solver executions", total: runs, x: points.runs[0], y: points.runs[1], r: bundleRadius(runs, compact), tone: "green" },
    { id: "artifacts", label: "Artifacts", sub: "plots + files", total: artifacts, x: points.artifacts[0], y: points.artifacts[1], r: bundleRadius(artifacts, compact), tone: "amber" },
    { id: "notes", label: "Notes", sub: "spool memory", total: notes, x: points.notes[0], y: points.notes[1], r: bundleRadius(notes, compact), tone: "muted" },
  ];
}

function samplePoints(total: number, radius: number, seed: number, max = 76): Array<{ x: number; y: number; lit: boolean }> {
  const count = Math.max(8, Math.min(max, Math.ceil(Math.sqrt(Math.max(total, 1)) * 4)));
  return Array.from({ length: count }, (_, index) => {
    const angle = index * 2.399963 + seed;
    const dist = Math.sqrt((index + 0.5) / count) * radius * 0.82;
    return {
      x: Math.cos(angle) * dist,
      y: Math.sin(angle) * dist,
      lit: index < Math.max(2, Math.min(8, Math.ceil(count * 0.12))),
    };
  });
}

function runLabel(data: V3WorkbenchData, selectedRunId: string | null): string | null {
  if (!selectedRunId) return null;
  const row = data.rows.find((candidate) => rowKey(candidate) === selectedRunId || candidate.runId === selectedRunId);
  return row?.runId ?? selectedRunId;
}

export function ThreadV5DagMinimap({ data, selectedRunId, noteCount = 0, onExpand }: ProvenanceProps) {
  const bundles = useMemo(() => makeBundles(data, noteCount, true), [data, noteCount]);
  const byId = new Map(bundles.map((bundle) => [bundle.id, bundle]));
  const hasSelection = Boolean(selectedRunId);
  return (
    <button className="v5-dag-card" type="button" onClick={onExpand}>
      <span className="v5-strip-title"><Icons.Dag size={13} />DAG minimap</span>
      <svg className="v5-dag-mini-svg" viewBox="0 0 232 112" aria-hidden="true">
        {BUNDLE_EDGES.map(([fromId, toId]) => {
          const from = byId.get(fromId);
          const to = byId.get(toId);
          return from && to ? (
            <line key={`${fromId}-${toId}`} className={hasSelection ? "is-lit" : ""} x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
          ) : null;
        })}
        {bundles.map((bundle, index) => (
          <g key={bundle.id} className={`v5-bundle-tone-${bundle.tone}`}>
            <circle className={hasSelection ? "is-dim" : ""} cx={bundle.x} cy={bundle.y} r={bundle.r} />
            {samplePoints(bundle.total, bundle.r, index).slice(0, 18).map((point, pointIndex) => (
              <circle
                key={pointIndex}
                className={hasSelection && point.lit ? "is-lit" : ""}
                cx={bundle.x + point.x}
                cy={bundle.y + point.y}
                r={point.lit && hasSelection ? 2.5 : 1.5}
              />
            ))}
          </g>
        ))}
      </svg>
      <span className="v5-muted">{hasSelection ? `lineage: ${runLabel(data, selectedRunId)}` : "project map"}</span>
    </button>
  );
}

export function ThreadV5BundleProvenance({ data, selectedRunId, noteCount = 0, full = false, onSelectRun }: ProvenanceProps) {
  const bundles = useMemo(() => makeBundles(data, noteCount, false), [data, noteCount]);
  const byId = new Map(bundles.map((bundle) => [bundle.id, bundle]));
  const selectedNodes = useMemo(() => selectedLineageNodeIds(data, selectedRunId), [data, selectedRunId]);
  const selectedLabel = runLabel(data, selectedRunId);
  const hasSelection = Boolean(selectedRunId || selectedNodes.size);
  const rowsForVertices = data.rows.slice(0, 12);

  return (
    <section className={`v5-provenance-canvas ${full ? "is-full" : ""}`}>
      <header className="v5-panel-header">
        <div>
          <span className="v5-eyebrow">Provenance</span>
          <h2>{full ? "Full provenance" : "Bundle provenance"}</h2>
        </div>
        <div className="v5-panel-actions">
          {selectedLabel ? <span className="v5-chip is-green">Focused: run {selectedLabel}</span> : <span className="v5-chip">Tier 0 project map</span>}
          <button className="v5-icon-button" type="button" title="2D bundle mode">2D</button>
          <button className="v5-icon-button" type="button" title="3D reserved">3D</button>
        </div>
      </header>
      <div className="v5-provenance-body">
        <svg className="v5-bundle-svg" viewBox="0 0 940 430" aria-label="Thread provenance bundle graph">
          {BUNDLE_EDGES.map(([fromId, toId, label]) => {
            const from = byId.get(fromId);
            const to = byId.get(toId);
            if (!from || !to) return null;
            const lit = hasSelection && (fromId === "runs" || toId === "runs" || fromId === "artifacts" || toId === "artifacts");
            const midX = (from.x + to.x) / 2;
            const midY = (from.y + to.y) / 2;
            return (
              <g key={`${fromId}-${toId}`} className={lit ? "is-lit" : ""}>
                <line className="v5-bundle-edge" x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
                <text className="v5-bundle-edge-label" x={midX} y={midY - 6}>{label}</text>
              </g>
            );
          })}
          {bundles.map((bundle, index) => {
            const points = samplePoints(bundle.total, bundle.r, index + 1, full ? 92 : 68);
            const bundleLit = hasSelection && (bundle.id === "runs" || bundle.id === "artifacts" || bundle.id === "meshes" || bundle.id === "geometry" || bundle.id === "notes");
            return (
              <g key={bundle.id} className={`v5-bundle v5-bundle-tone-${bundle.tone} ${bundleLit ? "is-focused" : ""}`}>
                <circle className="v5-bundle-shell" cx={bundle.x} cy={bundle.y} r={bundle.r} />
                <ellipse className="v5-bundle-glint" cx={bundle.x - bundle.r * 0.32} cy={bundle.y - bundle.r * 0.34} rx={bundle.r * 0.28} ry={bundle.r * 0.1} />
                {points.map((point, pointIndex) => {
                  const lit = hasSelection && (point.lit || (bundle.id === "runs" && pointIndex < rowsForVertices.length));
                  const run = rowsForVertices[pointIndex % Math.max(rowsForVertices.length, 1)];
                  return (
                    <circle
                      key={pointIndex}
                      className={lit ? "is-lit" : "is-ghost"}
                      cx={bundle.x + point.x}
                      cy={bundle.y + point.y}
                      r={lit ? 4.2 : 2.1}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (bundle.id === "runs" && run) onSelectRun?.(rowKey(run));
                      }}
                    />
                  );
                })}
                <text className="v5-bundle-label" x={bundle.x} y={bundle.y + bundle.r + 30}>{bundle.label}</text>
                <text className="v5-bundle-sub" x={bundle.x} y={bundle.y + bundle.r + 48}>{bundle.total.toLocaleString()} - {bundle.sub}</text>
              </g>
            );
          })}
        </svg>
        <aside className="v5-provenance-notes">
          <span className="v5-eyebrow">Tiering</span>
          <strong>{full ? "Tier 3: full graph" : selectedRunId ? "Tier 1: selected lineage" : "Tier 0: project map"}</strong>
          <p>
            V5 keeps the full Thread graph underneath, but the visible graph starts as bundles. Selection brings a clique forward; the rest stays present without turning into spaghetti.
          </p>
          <dl>
            <div><dt>Nodes represented</dt><dd>{data.provenance.representedScale.totalNodes.toLocaleString()}</dd></div>
            <div><dt>Edges represented</dt><dd>{data.provenance.representedScale.totalEdges.toLocaleString()}</dd></div>
            <div><dt>Selected lineage</dt><dd>{selectedNodes.size || (selectedRunId ? 5 : 0)}</dd></div>
          </dl>
        </aside>
      </div>
    </section>
  );
}
